import React from "react";
import { Box, Button, Text } from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import moment from "moment-timezone";
import styles from "./ReportCanvas.module.css";

export interface ReportHeaderActionsProps {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  /** Run timestamp (ISO) and the salon timezone it is shown in. */
  generatedAt?: string | null;
  timezone?: string | null;
}

/** "Updated Oct 7, 2:22 PM EDT" in the salon's timezone (named, since it can
 *  differ from the browser's). */
export function updatedLabel(
  generatedAt: string,
  timezone?: string | null,
): string {
  const at = moment.utc(generatedAt);
  if (!at.isValid()) return "";
  const local =
    timezone && moment.tz.zone(timezone) ? at.tz(timezone) : at.local();
  return `Updated ${local.format("MMM D, h:mm A z").trim()}`;
}

/**
 * ReportHeader's right-side slot for a v2 report: when the view has unsaved
 * changes, "Unsaved changes" with Discard and Save view; otherwise when the
 * shown result was computed.
 */
export default function ReportHeaderActions({
  dirty,
  saving,
  onSave,
  onDiscard,
  generatedAt,
  timezone,
}: ReportHeaderActionsProps) {
  if (!dirty) {
    return generatedAt ? (
      <Text className={styles.updated}>
        {updatedLabel(generatedAt, timezone)}
      </Text>
    ) : null;
  }
  return (
    <>
      <Box className={styles.unsaved} role="status">
        <Box component="span" className={styles.unsavedDot} aria-hidden />
        <Text component="span" inherit>
          Unsaved changes
        </Text>
      </Box>
      <Button
        variant="subtle"
        color="gray"
        onClick={onDiscard}
        disabled={saving}
      >
        Discard
      </Button>
      <Button
        color="teal"
        leftSection={<IconDeviceFloppy size={15} aria-hidden />}
        onClick={onSave}
        loading={saving}
        title="Save as this report's default view"
      >
        Save view
      </Button>
    </>
  );
}
